import { useEffect, useMemo, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { ArrowRight, BadgeCheck, Ban, CalendarClock, CalendarPlus, Check, ChevronLeft, CircleAlert, CircleX, Clock, Loader2, Lock, Megaphone, Scale, Search, Sparkles, Stethoscope, Wrench, X, Zap } from "lucide-react";
import { PhoneField, TimezoneSelect, BusinessLookupField, cityFromAddress, guessCountry, toE164, clampUsTz, splitName } from "./fields";
import { gcalLink, outlookLink, downloadIcs } from "@/lib/calendarLinks";
import { trackEvent, trackFunnel } from "@/lib/analytics";
import { getAffiliateCode } from "@/lib/affiliate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prefetch the strategy-call slots once and share the result, so the booking
// calendar is already loaded by the time the prospect reaches it. Call
// prefetchStrategySlots() early (e.g. when the report opens); the funnel reuses
// the same in-flight promise instead of firing its own fetch at the end.
let _slotsPromise = null;
export function prefetchStrategySlots() {
  if (!_slotsPromise) {
    _slotsPromise = fetch(`${API}/strategy/slots`)
      .then((r) => (r.ok ? r.json() : { slots: [] }))
      .then((d) => d.slots || [])
      .catch(() => []);
  }
  return _slotsPromise;
}

export const QUESTIONS = [
  { key: "work_type", q: "What kind of practice or business do you run?",
    opts: ["Legal or professional", "Medical, dental or chiro", "Med spa & wellness", "Home services & trades"],
    icons: [Scale, Stethoscope, Sparkles, Wrench] },
  { key: "runs_marketing", q: "Do you currently run any marketing?",
    sub: "There's no wrong answer — it just helps us come prepared.",
    opts: ["Yes — Meta / Google / YouTube / lead services", "No — nothing right now"],
    icons: [Megaphone, Ban] },
  // GBP question is informational only — answering "No" no longer disqualifies;
  // a business without a profile is still a fit (we build/claim it for them).
  { key: "has_gbp", q: "Do you have an existing Google Business Profile?",
    sub: "The listing that puts your business on Google Maps.",
    opts: ["Yes", "No"],
    icons: [BadgeCheck, CircleX] },
];

// Steps: 0 quiz -> 1 contact (name/phone, email auto-pops) -> 2 calendar -> 3 booked
const STAGES = ["Answer questions", "Your details", "Pick your time"];

const inputCls = "w-full rounded-xl border border-navy/12 bg-white px-4 py-3 text-[15px] outline-none transition-colors focus:border-brand-indigo/50 lg:px-5 lg:py-4 lg:text-base";

// Copy presets per surface — the ads lander sells the "competitor analysis",
// the main site sells the broader "market strategy call". No prices on either.
export const GETLEADS_COPY = {
  quizEyebrow: "Free onboarding call",
  quizHeadline: "Build My Free Growth Plan",
  quizSub: "We only take one business per trade per area.",
  contactEyebrow: "One step to your free onboarding call",
  contactHeading: "Book Your Free Onboarding Call",
  contactSub: "Name, email, cell and your business — then pick your time. No card, no catch.",
  consentAbout: "your free onboarding call",
  continueLabel: "Continue To Pick My Time",
  businessPlaceholder: "Business name (so we know who we're talking to)",
  calendarBadge: "Free onboarding call",
  calendarHeading: "Pick a time for your onboarding call",
  calendarSub: "A quick 15-minute onboarding call. Ask us anything about ranking #1 on AI, Google & Maps — no pressure, no sales pitch.",
  calendarIncludesTitle: "You leave with:",
  calendarIncludes: [
    "Your Google score — graded live",
    "Your geo-grid heatmap — where you show up, street by street",
    "Exactly who outranks you (their ratings + review counts)",
    "Your AI visibility across ChatGPT, Claude, Gemini, Grok, Meta AI & Perplexity",
  ],
  calendarCommit: "Quick heads up: due to high demand, this slot is held just for you the moment you book. Please only book a time you can actually make.",
  bookButton: "Book My Onboarding Call",
  eventTitle: "Slammed — Onboarding Call",
  eventDetails: "Your free onboarding call with Slammed — ask us anything about ranking #1 on AI, Google & Maps and getting more clients calling.",
  icsName: "slammed-qa-call.ics",
};

export const SITE_COPY = {
  quizEyebrow: "Quick 15-min onboarding call",
  quizHeadline: "Get Your Free Competitor Analysis",
  quizSub: "We only take one business per trade per area.",
  contactEyebrow: "Almost there — your details",
  contactHeading: "Who's Joining?",
  contactSub: "A quick onboarding call: ask us anything, we'll show you how you get more clients, and answer every question.",
  consentAbout: "your onboarding call",
  businessPlaceholder: "Business name (so we come prepared)",
  calendarBadge: "Quick 15-min onboarding call",
  calendarSub: "A quick onboarding call. Ask us anything and we'll map the fastest route to more clients.",
  calendarIncludes: [
    "Competitor teardown — who's winning your customers on Google right now",
    "Your Google Business Profile score, graded live",
    "Keyword heatmap of your area — where you show up and where you don't",
    "A clear next step, yours to keep either way",
  ],
  bookButton: "Book my onboarding call",
  calendarCommit: "Quick heads up: due to high demand, we prep your market map before the call and hold this slot just for you. Please only book a time you can actually make.",
  eventTitle: "Slammed — Onboarding Call",
  eventDetails: "Your free onboarding call with Slammed — ask us anything about getting more customers from Google.",
  icsName: "slammed-qa-call.ics",
};

// iClosed-style locked month calendar: real availability dots, blurred behind a
// "fill out the form first" overlay until the visitor reaches the calendar step.
function MiniCalendar({ slots, tz, testId = "mini-calendar" }) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const monthLabel = now.toLocaleDateString([], { month: "long", year: "numeric" });
  const firstDow = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" });
  const todayKey = fmt.format(now);
  const avail = new Set((slots || []).map((u) => fmt.format(new Date(u))));
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div data-testid={testId} className="relative mt-6 overflow-hidden rounded-2xl border-t border-navy/8 pt-6 lg:mt-0 lg:rounded-3xl lg:border lg:border-navy/10 lg:bg-mist/40 lg:p-6">
      <div className="flex items-center justify-between">
        <p className="font-display text-[15px] font-extrabold text-navy/70">{monthLabel}</p>
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-navy/35">Live availability</span>
      </div>
      <div className="mt-3 grid grid-cols-7 gap-1 text-center">
        {["SU", "MO", "TU", "WE", "TH", "FR", "SA"].map((w) => <span key={w} className="py-1 text-[10px] font-bold text-navy/30">{w}</span>)}
        {cells.map((d, i) => {
          if (!d) return <span key={`b${i}`} />;
          const key = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const open = avail.has(key);
          const past = key < todayKey;
          return (
            <span key={key} className={`relative flex h-9 flex-col items-center justify-center rounded-lg text-[12.5px] font-semibold ${past ? "text-navy/20" : "text-navy/60"}`}>
              {d}
              {open && <span className="mt-0.5 h-1 w-1 rounded-full bg-brand-indigo" />}
            </span>
          );
        })}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/55 backdrop-blur-[1.5px]">
        <p className="mx-6 flex items-center gap-2 rounded-2xl border border-navy/10 bg-white px-4 py-3 text-center text-[13px] font-bold text-navy/80 shadow-[0_12px_30px_-14px_rgba(14,18,48,0.25)]">
          <Lock className="h-4 w-4 shrink-0 text-brand-indigo" /> Fill out the form to unlock your time slot
        </p>
      </div>
    </div>
  );
}

// /rebook — no-quiz rebooking page (no-show follow-up texts/emails link here).
// Details -> calendar straight away; everything else inherits the site copy.
export const REBOOK_COPY = {
  ...SITE_COPY,
  contactEyebrow: "No problem — grab a new time",
  contactHeading: "Rebook Your Free Competitor Analysis Call",
  contactSub: "Same quick details, then pick any open slot. Your report is ready when you are.",
  calendarBadge: "Free competitor analysis call",
  qualifiedBadge: "", // hidden — rebooking isn't a qualification
  // rebookers already missed once — same respect frame, slightly firmer ask
  calendarCommit: "Quick heads up: due to high demand, this slot is held just for you and we rebuild your report before the call — please only grab a time you can keep this time.",
};

// The full booking funnel card: quiz -> details -> calendar -> confirmed.
// Shared by /getleads (ads lander), the main site (/contact + home bottom),
// and /rebook (skipQuiz — no questions, straight to details).
export default function StrategyFunnel({ id = "strategy-funnel-card", copy = GETLEADS_COPY, src = "getleads", showPreview = true, skipQuiz = false, wizard = false, open = true, onClose = null, paidFirst = false, prefill = null, bare = false, straightToCalendar = false }) {
  const [viewerTz, setViewerTz] = useState(() => clampUsTz(Intl.DateTimeFormat().resolvedOptions().timeZone));
  // paidFirst: lander trial flow — details only, then straight to checkout.
  // Booking happens AFTER payment on the kickoff page.
  // Quiz is shown FIRST for every entry point EXCEPT /rebook (skipQuiz), where we
  // already have the client's answers. prefill alone (e.g. the site/contact flow)
  // pre-fills the contact fields but no longer skips the questions.
  const [step, setStep] = useState(skipQuiz ? (prefill ? 2 : 1) : (paidFirst ? 1 : 0));
  const [quiz, setQuiz] = useState({});
  const [qIndex, setQIndex] = useState(0);
  const [form, setForm] = useState(() => {
    const dial = guessCountry(Intl.DateTimeFormat().resolvedOptions().timeZone).dial;
    const base = { first_name: "", phone: "", dial, email: "", business: "", website: "", company_website: "" };
    if (!prefill) return base;
    // E.164 from the scan gate -> dial + national number for the phone field
    const raw = (prefill.phone || "").replace(/[^\d+]/g, "");
    const m = raw.match(/^\+?1?(\d{10})$/);
    return { ...base, first_name: prefill.first_name || "", email: prefill.email || "", business: prefill.business || "", website: prefill.website || "",
      dial: raw.startsWith("+") && !m ? dial : "+1", phone: m ? m[1] : raw.replace(/^\+/, "") };
  });
  const [editDetails, setEditDetails] = useState(false); // prefilled details card -> inline edit
  const [confirmed, setConfirmed] = useState(!prefill || straightToCalendar || !skipQuiz); // prefill confirm card only for the skip-quiz (rebook) jump-to-calendar path
  const [bizPick, setBizPick] = useState(null); // {place_id, address} from the Google autocomplete
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [slots, setSlots] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [booked, setBooked] = useState(null);
  const [disqualified, setDisqualified] = useState(false); // answered No to the GBP qualifier
  const [slideDir, setSlideDir] = useState(1); // 1 = next screen slides in from right, -1 = back from left
  const startedRef = useRef(false);

  // Funnel start: no quiz anymore, so "get_started" fires the moment the booking
  // form is actually on screen (wizard opened, or inline funnel mounted).
  useEffect(() => {
    if (startedRef.current) return;
    if (step >= 1 && (!wizard || open)) { startedRef.current = true; trackEvent("get_started"); }
  }, [step, wizard, open]);

  // wizard mode: lock page scroll + Esc closes while the takeover is open.
  // iOS Safari ignores overflow:hidden on body — the fixed-position dance is
  // the only reliable background-scroll lock there.
  useEffect(() => {
    if (!wizard || !open) return undefined;
    const body = document.body;
    const y = window.scrollY;
    const prev = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };
    body.style.position = "fixed";
    body.style.top = `-${y}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, y);
      window.removeEventListener("keydown", onKey);
    };
  }, [wizard, open, onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let alive = true;
    prefetchStrategySlots().then((s) => { if (alive) setSlots(s); });
    return () => { alive = false; };
  }, []);

  const days = useMemo(() => {
    const map = {};
    (slots || []).forEach((utc) => {
      const d = new Date(utc);
      const key = d.toLocaleDateString("en-CA", { timeZone: viewerTz });
      if (!map[key]) map[key] = {
        key,
        label: d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", timeZone: viewerTz }),
        times: [],
      };
      map[key].times.push({ utc, label: d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: viewerTz }) });
    });
    return Object.values(map);
  }, [slots, viewerTz]);
  useEffect(() => { if (days.length && !activeDay) setActiveDay(days[0].key); }, [days, activeDay]);
  const activeTimes = days.find((d) => d.key === activeDay)?.times || [];
  const activeLabel = days.find((d) => d.key === activeDay)?.label || "";

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  // Phone + business + website pop up on their own the moment name + a valid
  // email are in — no Continue click.
  const emailRevealed = form.first_name.trim().length > 1 && emailValid;
  const phoneReady = form.phone.replace(/\D/g, "").length >= 7;
  // trial flow also collects the business name (via Google autocomplete) up front
  const businessReady = form.business.trim().length > 1;

  // ---- step 0: quiz (auto-advance on tap, last answer -> contact step)
  const [optLock, setOptLock] = useState(false); // brief pointer lock AFTER advancing so the option under the cursor/finger on the next question doesn't look pre-highlighted
  const pick = (key, val) => {
    if (qIndex === 0 && !Object.keys(quiz).length) trackEvent("get_started"); // funnel: quiz started
    trackEvent(`quiz_q${qIndex + 1}`); // per-question drop-off tracking
    trackEvent(`quiz_ans:${qIndex + 1}:${val}`); // which answer was picked (answer-mix breakdown)
    setSlideDir(1);
    const next = { ...quiz, [key]: val };
    setQuiz(next);
    const q = QUESTIONS[qIndex];
    const isDq = q.disqualifyOn && val === q.disqualifyOn;
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    setTimeout(() => {
      if (isDq) { setDisqualified(true); return; }
      if (qIndex < QUESTIONS.length - 1) setQIndex(qIndex + 1);
      else {
        // final answer -> mini celebration before the details step (big confetti
        // is saved for the actual booking)
        confetti({ colors: ["#5457E5", "#8B5CF6", "#31A8F0", "#22C55E"], particleCount: 45, spread: 55, startVelocity: 26, origin: { y: 0.65 }, disableForReducedMotion: true });
        setStep(1);
      }
      setOptLock(true);
      setTimeout(() => setOptLock(false), 350);
    }, 400);
  };

  // ---- step 1: name + phone (+ auto-revealed email, business, website) -> calendar
  const submitContact = async () => {
    const e164 = toE164(form.dial, form.phone);
    if (!form.first_name.trim() || e164.replace(/\D/g, "").length < 10) {
      setError("Add your name and a real cell number.");
      return;
    }
    if (!emailValid) { setError("Add a valid email — that's where your invite goes."); return; }
    setBusy(true); setError("");
    const { first, last } = splitName(form.first_name);
    try {
      const r = await fetch(`${API}/getleads/capture`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: first, last_name: last, phone: e164, company_website: form.company_website, funnel: src, a: getAffiliateCode() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(typeof d.detail === "string" ? d.detail : "Check your number and try again."); return; }
      let leadEventId = "";
      if (d.lead_id) {
        const ar = await fetch(`${API}/getleads/answers`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lead_id: d.lead_id, quiz, email: form.email.trim(), business: form.business.trim(), website: form.website.trim(),
            business_place_id: bizPick?.place_id || "", business_city: bizPick?.address ? cityFromAddress(bizPick.address) : "", a: getAffiliateCode() }),
        });
        await ar.json().catch(() => ({}));
      }
      trackFunnel("lead_submitted"); // funnel: contact details captured (attributed to origin)
      if (paidFirst) {
        // trial flow: lead captured -> straight to the $1 checkout with their
        // details (Whop prefills + /api/kickoff/return mints their session)
        trackEvent("checkout_started");
        const q = new URLSearchParams({ e: form.email.trim(), n: form.first_name.trim(), p: e164 });
        if (form.business.trim()) q.set("b", form.business.trim());
        window.location.assign(`/start?plan=pro&${q.toString()}`);
        return;
      }
      setStep(2);
    } finally { setBusy(false); }
  };

  // ---- step 2: book the call
  const book = async () => {
    if (!chosen || busy) return;
    if (!businessReady) { setError("Add your business name so we know who we're talking to."); return; }
    setBusy(true); setError("");
    const { first, last } = splitName(form.first_name);
    try {
      const r = await fetch(`${API}/strategy/book`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: first, last_name: last, email: form.email.trim(), phone: toE164(form.dial, form.phone),
          business: form.business.trim(), company_website: form.company_website, website: form.website.trim(),
          business_place_id: bizPick?.place_id || "",
          business_city: bizPick?.address ? cityFromAddress(bizPick.address) : "",
          timezone: viewerTz, start_utc: chosen.utc, quiz, funnel: src,
          start_local_label: `${activeLabel} at ${chosen.label} (${viewerTz})`,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        trackFunnel("call_booked"); // funnel outcome: booked the 30-min call (attributed to origin)
        setBooked({ meet_url: data.meet_url || "", label: `${activeLabel} at ${chosen.label}`, start_utc: chosen.utc });
        setStep(3);
        confetti({ colors: ["#5457E5", "#8B5CF6", "#31A8F0", "#22C55E"], particleCount: 90, spread: 75, disableForReducedMotion: true });
        // every booking lands on the dedicated thank-you page (pre-call prep +
        // clean ad-conversion URL). Step 3 stays as the no-JS-navigation fallback.
        const qs = new URLSearchParams({ name: form.first_name.trim(), when: `${activeLabel} at ${chosen.label} (${viewerTz})`, start: chosen.utc });
        if (data.meet_url) qs.set("meet", data.meet_url);
        window.location.assign(`/thanks?${qs.toString()}`);
      } else {
        setError(typeof data.detail === "string" ? data.detail : "That time was just taken — pick another.");
      }
    } finally { setBusy(false); }
  };

  const progress = Math.min(step / 3, 1);
  const stages = paidFirst ? ["Your details"] : skipQuiz ? ["Your details", "Pick your time"] : STAGES;
  const stageOffset = skipQuiz ? 1 : 0;

  // ---- wizard (full-screen takeover on /getleads) helpers ----
  const confirming = step === 2 && !!prefill && !confirmed; // audit modal: "Is this correct?" before the calendar
  const wizardLabel = disqualified || step === 3 ? "" : step === 0 ? `Question ${qIndex + 1} of ${QUESTIONS.length}` : step === 1 ? "Your details" : confirming ? "Confirm your details" : "Pick your time";
  // paidFirst: one step only — bar starts slim and fills as the 3 fields complete
  const paidFirstProgress = (form.first_name.trim().length > 1 ? 0.34 : 0.08) + (emailRevealed ? 0.33 : 0) + (phoneReady ? 0.33 : 0);
  const wizardProgress = paidFirst ? paidFirstProgress : step === 3 ? 1 : confirming ? 0.5 : step === 2 ? 0.85 : step === 1 ? 0.55 : Math.max(0.07, (qIndex / QUESTIONS.length) * 0.34);
  const wizardBack = () => {
    setSlideDir(-1);
    if (prefill && step === 2) {
      // audit-story modal: calendar -> "Is this correct?" -> back to the audit slide
      if (confirmed) { setConfirmed(false); setEditDetails(false); }
      else onClose?.();
    }
    else if (step === 0 && qIndex > 0) setQIndex(qIndex - 1);
    else if (step === 1 && !paidFirst && !skipQuiz) { setStep(0); setQIndex(QUESTIONS.length - 1); }
    else if (step === 2) setStep(1);
  };
  // paidFirst / skipQuiz: details is the first step — there is no quiz to back into
  const wizardCanBack = !disqualified && step < 3 && (paidFirst || skipQuiz ? step > 1 : (step > 0 || qIndex > 0));

  // wizard modal: browser/phone back gesture closes the takeover instead of
  // leaving the page. One history entry is pushed on open and consumed on close.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!wizard || !open || !onCloseRef.current) return undefined;
    // deferred so StrictMode's mount/unmount/mount in dev doesn't push twice
    let pushed = false;
    const onPop = () => onCloseRef.current?.();
    const t = setTimeout(() => {
      window.history.pushState({ ...(window.history.state || {}), slFunnel: true }, "");
      pushed = true;
      window.addEventListener("popstate", onPop);
    }, 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener("popstate", onPop);
      if (pushed && window.history.state?.slFunnel) window.history.back();
    };
  }, [wizard, open]);

  const qWords = QUESTIONS[qIndex].q.split(" "); // word-by-word headline pop (re-runs per question via the keyed body below)
  const body = (
      <div className={wizard ? "w-full" : bare ? "pb-6" : `p-6 sm:p-8 ${showPreview && step < 2 ? "lg:grid lg:grid-cols-[1.05fr_1fr] lg:items-center lg:gap-10 lg:p-10" : ""}`} key={step === 0 ? `q-${qIndex}` : `${step}-${confirmed ? 1 : 0}`}>
        {/* disqualified spans both desktop columns so it sits centered, not squished left */}
        <div className={`${slideDir < 0 ? "funnel-step-back" : "funnel-step"} ${disqualified && !wizard ? "lg:col-span-2" : ""}`}>

          {disqualified && (
            <div data-testid="funnel-disqualified" className="py-6 text-center lg:py-10">
              <span data-testid="disqualified-badge" className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/10 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-rose-500">
                <CircleAlert className="h-3.5 w-3.5" /> Not a fit yet
              </span>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-navy lg:text-4xl">Start with your Google profile</h2>
              <p className="mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-navy/60 lg:text-base">
                Our system works by optimizing your Google Business Profile, so you need one before we can help. It is free to create at{" "}
                <a href="https://google.com/business" target="_blank" rel="noreferrer" className="font-bold text-brand-indigo underline">google.com/business</a>.
                Get it verified, then come back and we will take it from there.
              </p>
              <button data-testid="disqualified-start-over"
                onClick={() => { setDisqualified(false); setQuiz({}); setQIndex(0); }}
                className="mt-6 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-navy/45 transition-colors hover:text-navy/80">
                <ChevronLeft className="h-3.5 w-3.5" /> Start over
              </button>
            </div>
          )}

          {!disqualified && step === 0 && (
            <div data-testid="funnel-quiz">
              {!wizard && (
                <>
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-navy/40 lg:text-[11px]">{copy.quizEyebrow}</p>
                  <h2 data-testid="quiz-headline" className="mt-2 font-display text-2xl font-extrabold tracking-tight text-navy lg:text-3xl">
                    {copy.quizHeadline.split("Free").map((part, i, arr) => (
                      <span key={i}>{part}{i < arr.length - 1 && <span className="bg-brand-gradient bg-clip-text text-transparent">Free</span>}</span>
                    ))}
                  </h2>
                  <p className="mt-2 text-sm text-navy/55 lg:text-base">{copy.quizSub}</p>
                </>
              )}
              <h3 className={wizard
                ? "text-center font-display text-[1.65rem] font-extrabold leading-tight tracking-tight text-navy sm:text-3xl lg:text-4xl"
                : "mt-6 border-t border-navy/8 pt-5 font-display text-lg font-bold tracking-tight text-navy lg:mt-8 lg:pt-6 lg:text-xl"}>
                {qWords.map((w, i) => (
                  <span key={i} className="sl-word-pop" style={{ animationDelay: `${0.12 + i * 0.045}s` }}>{w}{i < qWords.length - 1 ? " " : ""}</span>
                ))}
              </h3>
              {QUESTIONS[qIndex].sub && <p className={`mt-1.5 text-[13px] font-medium text-navy/50 lg:text-[15px] ${wizard ? "text-center" : ""}`}>{QUESTIONS[qIndex].sub}</p>}
              <div className={`mt-5 space-y-2.5 lg:mt-7 lg:space-y-3 ${optLock ? "pointer-events-none" : ""}`}>
                {QUESTIONS[qIndex].opts.map((o, i) => {
                  const OptIcon = (QUESTIONS[qIndex].icons || [])[i];
                  const selected = quiz[QUESTIONS[qIndex].key] === o;
                  return (
                    <button key={o} data-testid={`quiz-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}`}
                      onClick={() => pick(QUESTIONS[qIndex].key, o)}
                      style={wizard ? { animation: `funnelIn .45s cubic-bezier(.16,1.15,.3,1) ${0.1 + i * 0.08}s both` } : undefined}
                      className={`sl-opt flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3.5 text-left text-[15px] font-semibold transition-all duration-200 lg:px-6 lg:py-5 lg:text-lg ${wizard ? "sm:py-5 sm:text-lg" : ""} ${
                        selected
                          ? "sl-opt-pop scale-[1.02] border-brand-indigo bg-brand-indigo/10 text-brand-indigo ring-2 ring-brand-indigo/60"
                          : "border-navy/10 text-navy/75 hover:-translate-y-0.5 hover:border-brand-indigo/40 hover:bg-brand-indigo/[0.03]"
                      }`}>
                      <span className="flex min-w-0 items-center gap-3 lg:gap-4">
                        {OptIcon && (
                          <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl transition-colors duration-200 lg:h-10 lg:w-10 ${selected ? "bg-brand-gradient text-white shadow-pill" : "bg-navy/5 text-navy/45"}`}>
                            <OptIcon className="h-[18px] w-[18px]" />
                          </span>
                        )}
                        <span className="min-w-0">{o}</span>
                      </span>
                      {selected && <span className="sl-check-burst shrink-0"><Check className="h-4 w-4" /></span>}
                    </button>
                  );
                })}
              </div>
              {!wizard && qIndex > 0 && (
                <button onClick={() => { setSlideDir(-1); setQIndex(qIndex - 1); }} className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-semibold text-navy/40 hover:text-navy/70">
                  <ChevronLeft className="h-3.5 w-3.5" /> Back
                </button>
              )}
            </div>
          )}

          {!disqualified && step === 1 && (
            <div data-testid="funnel-contact">
              <p data-testid="contact-eyebrow" className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-navy/40 lg:text-[11px]">{copy.contactEyebrow}</p>
              <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-navy lg:text-3xl">
                {copy.contactHeading.split("Free").map((part, i, arr) => (
                  <span key={i}>{part}{i < arr.length - 1 && <span className="bg-brand-gradient bg-clip-text text-transparent">Free</span>}</span>
                ))}
              </h2>
              <p className="mt-2 text-sm text-navy/55 lg:text-base">{copy.contactSub}</p>
              {copy.trialIncludes?.length ? (
                <div data-testid="funnel-trial-includes" className="mt-4 rounded-2xl border border-emerald-500/25 bg-emerald-50/60 p-4 lg:mt-5">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700 lg:text-[11px]">{copy.trialIncludesTitle}</p>
                  <ul className="mt-2.5 space-y-1.5">
                    {copy.trialIncludes.map((line) => (
                      <li key={line} className="flex items-start gap-2 text-[13.5px] font-semibold text-navy/75 lg:text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                  {copy.trialRiskReversal && (
                    <p className="mt-3 border-t border-emerald-500/20 pt-2.5 text-[12.5px] font-medium leading-relaxed text-navy/60">{copy.trialRiskReversal}</p>
                  )}
                </div>
              ) : null}
              <div className="mt-5 space-y-3 lg:mt-7 lg:space-y-4">
                <input data-testid="funnel-first-name" placeholder="Name" value={form.first_name} onChange={set("first_name")} className={inputCls} />
                <input data-testid="funnel-email" placeholder="Email (your invite goes here)" type="email" value={form.email} onChange={set("email")} className={inputCls} />
                {emailRevealed && (
                  <div className="funnel-step space-y-3" data-testid="funnel-email-block">
                    <PhoneField testId="funnel-phone" dial={form.dial} number={form.phone}
                      onDial={(v) => setForm((f) => ({ ...f, dial: v }))}
                      onNumber={(v) => setForm((f) => ({ ...f, phone: v }))} />
                  </div>
                )}
              </div>
              {/* honeypot — hidden off-screen; humans never see or fill it. FLAG-ONLY:
                  a trip is logged for review but never blocks the submission. */}
              <input value={form.company_website} onChange={set("company_website")} tabIndex={-1} autoComplete="off" className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" />
              {emailRevealed && (
                <p className="mt-3 text-[11px] leading-relaxed text-navy/40">
                  By continuing you agree to be contacted about {copy.consentAbout} by text and email, and to our{" "}
                  <a href="/terms.html" className="font-bold underline" target="_blank" rel="noreferrer">Terms</a> &amp;{" "}
                  <a href="/privacy.html" className="font-bold underline" target="_blank" rel="noreferrer">Privacy Policy</a>.
                  Msg &amp; data rates may apply.
                </p>
              )}
              {error && <p data-testid="funnel-error" className="mt-3 text-[13px] font-semibold text-rose-600">{error}</p>}
              <button data-testid="funnel-continue" onClick={submitContact} disabled={busy || !emailRevealed || !phoneReady}
                className="sl-glow-cta mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-gradient px-6 py-3.5 font-display text-[15px] font-semibold text-white shadow-pill transition-all hover:-translate-y-0.5 disabled:opacity-40">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {copy.continueLabel || "Continue to booking"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {!disqualified && step === 2 && prefill && !confirmed && (
            <div data-testid="prefill-confirm" className="mx-auto max-w-md">
              <h2 className="text-center font-display text-2xl font-extrabold tracking-tight text-navy">Is this <span className="bg-brand-gradient bg-clip-text text-transparent">correct?</span></h2>
              <p className="mt-2 text-center text-sm text-navy/55">We'll send your meeting invite and reminders to this email and text this number.</p>
              <div className="mt-5">
                  <div data-testid="prefill-details" className="rounded-2xl border border-navy/10 bg-white p-4 text-left shadow-sm">
                    <div className="flex items-center justify-between">
                      <p className="text-[10.5px] font-extrabold uppercase tracking-[0.16em] text-navy/40">Your details</p>
                      <button type="button" data-testid="prefill-edit" onClick={() => setEditDetails((v) => !v)} className="text-[12.5px] font-bold text-brand-indigo hover:underline">{editDetails ? "Done" : "Edit"}</button>
                    </div>
                    {editDetails ? (
                      <div className="mt-3 grid gap-2.5">
                        <input data-testid="prefill-name" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} placeholder="Name" className="h-11 rounded-xl border border-navy/15 px-3.5 text-[15px] text-navy outline-none focus:border-brand-indigo" />
                        <PhoneField testId="prefill-phone" dial={form.dial} number={form.phone} onDial={(v) => setForm((f) => ({ ...f, dial: v }))} onNumber={(v) => setForm((f) => ({ ...f, phone: v }))} />
                        <input data-testid="prefill-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="h-11 rounded-xl border border-navy/15 px-3.5 text-[15px] text-navy outline-none focus:border-brand-indigo" />
                        <input data-testid="prefill-business" value={form.business} onChange={(e) => setForm((f) => ({ ...f, business: e.target.value }))} placeholder="Business name" className="h-11 rounded-xl border border-navy/15 px-3.5 text-[15px] text-navy outline-none focus:border-brand-indigo" />
                      </div>
                    ) : (
                      <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13.5px]">
                        <dt className="font-semibold text-navy/45">Name</dt><dd className="truncate font-bold text-navy" data-testid="prefill-view-name">{form.first_name || "—"}</dd>
                        <dt className="font-semibold text-navy/45">Phone</dt><dd className="truncate font-bold text-navy" data-testid="prefill-view-phone">{form.phone ? `${form.dial} ${form.phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")}` : "—"}</dd>
                        <dt className="font-semibold text-navy/45">Email</dt><dd className="truncate font-bold text-navy" data-testid="prefill-view-email">{form.email || "—"}</dd>
                        <dt className="font-semibold text-navy/45">Business</dt><dd className="truncate font-bold text-navy" data-testid="prefill-view-business">{form.business || "—"}</dd>
                      </dl>
                    )}
                  </div>
              </div>
              <button type="button" data-testid="prefill-continue" onClick={() => { setEditDetails(false); setConfirmed(true); }}
                className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-navy font-display text-[15px] font-extrabold text-white transition-colors hover:bg-[#1b2050]">
                Yes, continue <ArrowRight className="h-4 w-4" />
              </button>
              {wizard && onClose && (
                <button type="button" data-testid="prefill-back-to-audit" onClick={onClose} className="mt-3 flex w-full items-center justify-center gap-1 text-[13px] font-bold text-navy/50 transition-colors hover:text-navy">
                  <ChevronLeft className="h-3.5 w-3.5" /> Back to my audit
                </button>
              )}
            </div>
          )}
          {!disqualified && step === 2 && confirmed && (
            <div data-testid="funnel-calendar" className={straightToCalendar
              ? "mx-auto flex max-w-xl flex-col gap-6"
              : "lg:grid lg:grid-cols-[1fr_1.15fr] lg:items-start lg:gap-10"}>
              {/* left rail (desktop): offer, context, what-you-get */}
              <div>
                {!straightToCalendar && (
                <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                  {(copy.qualifiedBadge ?? "You're qualified") && (
                    <span data-testid="qualified-badge" className="inline-flex items-center gap-1.5 rounded-full border-2 border-emerald-500/50 bg-emerald-500/10 px-4 py-2 font-display text-[13px] font-extrabold uppercase tracking-[0.08em] text-emerald-600">
                      <Check className="h-4 w-4" /> {copy.qualifiedBadge ?? "You're qualified"}
                    </span>
                  )}
                </div>
                )}
                {!straightToCalendar && (<>
                <p data-testid="calendar-eyebrow" className="mt-4 text-center font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-navy/40 lg:mt-5 lg:text-left lg:text-[13px]">{copy.calendarBadge}</p>
                <h2 className="mt-2 text-center font-display text-2xl font-extrabold tracking-tight text-navy lg:text-left lg:text-3xl" data-testid="calendar-heading">
                  {copy.calendarHeading ? (
                    <>{copy.calendarHeading.split("Google competitor report").map((part, i, arr) => (
                      <span key={i}>{part}{i < arr.length - 1 && <span className="bg-brand-gradient bg-clip-text text-transparent">Google competitor report</span>}</span>
                    ))}</>
                  ) : (<>Pick a time <span className="bg-brand-gradient bg-clip-text text-transparent">that works for you</span></>)}
                </h2>
                <p className="mt-2 text-center text-sm text-navy/55 lg:text-left lg:text-base">{copy.calendarSub}</p>
                </>)}
                {!straightToCalendar && (
                <div data-testid="calendar-call-includes" className="mx-auto mt-4 max-w-md rounded-2xl border border-navy/8 bg-navy/[0.02] p-4 lg:mx-0 lg:p-5">
                  <p className="text-[10.5px] font-extrabold uppercase tracking-[0.16em] text-navy/40">{copy.calendarIncludesTitle || "What you get on this call"}</p>
                  <ul className="mt-2 grid gap-x-4 gap-y-1.5 text-left sm:grid-cols-2 lg:grid-cols-1 lg:gap-y-2.5">
                    {(copy.calendarIncludes || []).map((t) => (
                      <li key={t} className="flex items-start gap-1.5 text-[12px] font-semibold leading-snug text-navy/65 lg:text-[14px]">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500 lg:h-4 lg:w-4" />{t}
                      </li>
                    ))}
                  </ul>
                </div>
                )}
                {prefill && (
                  <div data-testid="calendar-your-info" className="mx-auto mt-4 max-w-md rounded-2xl border border-navy/10 bg-white p-4 lg:mx-0">
                    <div className="flex items-center justify-between">
                      <p className="text-[10.5px] font-extrabold uppercase tracking-[0.16em] text-navy/40">Booking as — is this right?</p>
                      <button type="button" data-testid="calendar-info-edit" onClick={() => setEditDetails((v) => !v)} className="text-[12px] font-bold text-brand-indigo hover:underline">{editDetails ? "Done" : "Edit"}</button>
                    </div>
                    {editDetails ? (
                      <div className="mt-3 grid gap-2.5">
                        <input data-testid="calendar-info-name" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} placeholder="Name" className="h-11 rounded-xl border border-navy/15 px-3.5 text-[15px] text-navy outline-none focus:border-brand-indigo" />
                        <PhoneField testId="calendar-info-phone" dial={form.dial} number={form.phone} onDial={(v) => setForm((f) => ({ ...f, dial: v }))} onNumber={(v) => setForm((f) => ({ ...f, phone: v }))} />
                        <input data-testid="calendar-info-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="h-11 rounded-xl border border-navy/15 px-3.5 text-[15px] text-navy outline-none focus:border-brand-indigo" />
                      </div>
                    ) : (
                      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13px]">
                        <dt className="font-semibold text-navy/45">Name</dt><dd className="truncate font-bold text-navy" data-testid="calendar-info-view-name">{form.first_name || "—"}</dd>
                        <dt className="font-semibold text-navy/45">Phone</dt><dd className="truncate font-bold text-navy" data-testid="calendar-info-view-phone">{form.phone ? `${form.dial} ${form.phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")}` : "—"}</dd>
                        <dt className="font-semibold text-navy/45">Email</dt><dd className="truncate font-bold text-navy" data-testid="calendar-info-view-email">{form.email || "—"}</dd>
                      </dl>
                    )}
                  </div>
                )}
                <TimezoneSelect testId="funnel-tz" value={viewerTz} onChange={setViewerTz} />
                <div className="mx-auto mt-4 max-w-md space-y-2.5 lg:mx-0" data-testid="funnel-business-block">
                  <BusinessLookupField testId="funnel-business" value={form.business}
                    onChange={(v) => { setForm((f) => ({ ...f, business: v })); setBizPick(null); }}
                    onPick={(p) => { setForm((f) => ({ ...f, business: p.name, website: f.website || p.website || "" })); setBizPick({ place_id: p.place_id || "", address: p.address || "" }); }}
                    placeholder={copy.businessPlaceholder} inputClass={inputCls} />
                  {bizPick?.address && (
                    <p data-testid="funnel-biz-matched" className="-mt-1.5 flex items-center gap-1.5 pl-1 text-[11.5px] font-bold text-emerald-600">
                      <Check className="h-3.5 w-3.5" /> Exact listing matched — address auto-fills from Google
                    </p>
                  )}
                  <input data-testid="funnel-website" placeholder="Website (if you have one)" value={form.website} onChange={set("website")} className={inputCls} />
                </div>
              </div>
              {/* right rail (desktop): day/time picker + book */}
              <div>
                {slots === null ? (
                  <div className="flex items-center gap-2 py-8 text-sm text-navy/50"><Loader2 className="h-4 w-4 animate-spin" /> Loading open times…</div>
                ) : days.length === 0 ? (
                  <p className="py-6 text-sm text-navy/55">No open times this week — email <a className="font-semibold text-brand-indigo" href="mailto:help@tryslammed.io">help@tryslammed.io</a> and we'll fit you in.</p>
                ) : (
                  <>
                    {/* exactly 3 open days, always visible — matches the connected GHL calendar's 3-day booking window */}
                    <div className="mt-4 grid grid-cols-3 gap-2 lg:mt-0 lg:gap-2.5" data-testid="funnel-day-strip">
                      {days.slice(0, 3).map((d) => {
                        const [wd, rest] = d.label.split(", ");
                        return (
                          <button key={d.key} data-testid={`funnel-day-${d.key}`} onClick={() => { setActiveDay(d.key); setChosen(null); }}
                            className={`flex flex-col items-center rounded-xl border px-2 py-2.5 transition-colors lg:py-3 ${
                              activeDay === d.key ? "border-brand-indigo bg-brand-indigo/10 text-brand-indigo" : "border-navy/10 text-navy/60 hover:border-navy/25"}`}>
                            <span className="font-mono text-[9.5px] font-bold uppercase tracking-[0.12em] opacity-70">{wd}</span>
                            <span className="text-[14px] font-extrabold lg:text-[15px]">{rest}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-navy/40">
                      {activeLabel ? `Open times · ${activeLabel}` : "Open times"}
                    </p>
                    <div className="mt-2 grid grid-cols-3 gap-2 lg:gap-2.5">
                      {activeTimes.map((t) => (
                        <button key={t.utc} data-testid={`funnel-time-${t.label.replace(/\s/g, "")}`} onClick={() => setChosen(t)}
                          className={`rounded-xl border px-2 py-2.5 text-[13px] font-semibold transition-colors lg:py-3.5 lg:text-[15px] ${
                            chosen?.utc === t.utc ? "border-brand-indigo bg-brand-indigo text-white" : "border-navy/10 text-navy/70 hover:border-brand-indigo/40"}`}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                    {chosen && (
                      <p className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold text-navy/60 lg:text-[15px]">
                        <Clock className="h-3.5 w-3.5 text-brand-indigo lg:h-4 lg:w-4" /> {activeLabel} at {chosen.label}
                      </p>
                    )}
                    {error && <p data-testid="funnel-book-error" className="mt-3 text-[13px] font-semibold text-rose-600">{error}</p>}
                    <button data-testid="funnel-book" onClick={book} disabled={!chosen || busy}
                      className="sl-glow-cta mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-gradient px-6 py-3.5 font-display text-[15px] font-semibold text-white shadow-pill transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0 lg:py-4 lg:text-lg">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {copy.bookButton}
                    </button>
                    {copy.calendarCommit && (
                      <p data-testid="calendar-commit-note" className="mt-3 flex items-start gap-1.5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-left text-[12px] font-semibold leading-snug text-amber-800 lg:text-[12.5px]">
                        <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {copy.calendarCommit}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {step === 3 && booked && (
            <div data-testid="funnel-booked" className="py-4 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white">
                <Check className="h-7 w-7" />
              </span>
              <p data-testid="booked-eyebrow" className="mt-5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-navy/40 lg:text-[11px]">
                You're all set
              </p>
              <h2 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-navy lg:text-3xl">
                You're <span className="bg-brand-gradient bg-clip-text text-transparent">Booked</span>, {form.first_name}!
              </h2>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-navy/60">
                {`${booked.label}. It's a Google Meet video call — an invite is headed to your email, just click to join at that time. 15 minutes: ask us anything, and we'll map how you get more clients.`}
              </p>
              {/* add-to-calendar: Google / Apple (.ics) / Outlook */}
              {(() => {
                const details = copy.eventDetails;
                const gcal = gcalLink(booked.start_utc, copy.eventTitle, details, booked.meet_url);
                const outlook = outlookLink(booked.start_utc, copy.eventTitle, details, booked.meet_url);
                const btnCls = "inline-flex items-center gap-1.5 rounded-full border border-navy/12 bg-white px-4 py-2.5 text-[12.5px] font-bold text-navy/70 transition-all hover:-translate-y-0.5 hover:border-brand-indigo/40 hover:text-brand-indigo";
                return (
                  <div className="mt-6">
                    <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-navy/40">Add it to your calendar so you don't miss it</p>
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                      <a href={gcal} target="_blank" rel="noreferrer" data-testid="addcal-google" className={btnCls}><img src="https://cdn.simpleicons.org/googlecalendar" alt="" className="h-4 w-4" /> Google</a>
                      <button onClick={() => downloadIcs(copy.icsName, booked.start_utc, copy.eventTitle, details, booked.meet_url)} data-testid="addcal-apple" className={btnCls}><img src="https://cdn.simpleicons.org/apple" alt="" className="h-4 w-4" /> Apple</button>
                      <a href={outlook} target="_blank" rel="noreferrer" data-testid="addcal-outlook" className={btnCls}><img src="/outlook-icon.svg" alt="" className="h-4 w-4" /> Outlook</a>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
        {/* locked iClosed-style availability preview inside the same card until they reach the calendar */}
        {showPreview && !wizard && step < 2 && !disqualified && <MiniCalendar slots={slots} tz={viewerTz} />}
      </div>
  );

  // ---- full-screen wizard presentation (/getleads only): each question gets
  // its own screen, sliding; header = back / progress / close ----
  if (wizard) {
    if (!open) return null; // state stays alive — closing and reopening keeps answers
    return (
      <div id={id} className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-white" data-testid="getleads-wizard"
        style={{ backgroundImage: "radial-gradient(rgba(14,18,48,0.07) 1.2px, transparent 1.2px)", backgroundSize: "22px 22px" }}>
        <div className="sticky top-0 z-10 border-b border-navy/5 bg-white/85 backdrop-blur">
          <div className="mx-auto flex max-w-xl items-center gap-3 px-5 py-3.5 lg:max-w-4xl">
            {wizardCanBack ? (
              <button type="button" data-testid="wizard-back" onClick={wizardBack} aria-label="Back"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-navy/10 text-navy/60 transition-colors hover:border-navy/25 hover:text-navy">
                <ChevronLeft className="h-4 w-4" />
              </button>
            ) : <span className="h-9 w-9 shrink-0" />}
            <div className="h-1.5 flex-1 rounded-full bg-navy/5">
              <div className="sl-progress-shine h-full rounded-full bg-brand-gradient transition-all duration-500" style={{ width: `${wizardProgress * 100}%` }} data-testid="wizard-progress" />
            </div>
            <button type="button" data-testid="wizard-close" onClick={onClose} aria-label="Close"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-navy/10 text-navy/60 transition-colors hover:border-rose-300 hover:text-rose-500">
              <X className="h-4 w-4" />
            </button>
          </div>
          {wizardLabel && (
            <p className="pb-2.5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-navy/40" data-testid="wizard-step-label">
              <span key={wizardLabel} className="sl-word-pop">{wizardLabel}</span>
            </p>
          )}
        </div>
        <main className={`mx-auto flex min-h-[calc(100dvh-130px)] w-full max-w-xl flex-col justify-center px-5 pb-16 pt-8 sm:px-8 ${step === 2 ? "lg:max-w-4xl" : ""}`} data-testid="strategy-funnel">
          {body}
        </main>
      </div>
    );
  }

  return (
    <div id={id} className={bare
      ? "mx-auto w-full max-w-xl scroll-mt-24 lg:max-w-4xl"
      : "sl-funnel-frame mx-auto w-full max-w-xl scroll-mt-24 overflow-hidden rounded-3xl bg-white shadow-[0_24px_70px_-20px_rgba(14,18,48,0.18)] lg:max-w-4xl"} data-testid="strategy-funnel">
      {/* iClosed-style stage stepper */}
      {step < 3 && !disqualified && !straightToCalendar && (
        <div className="flex items-center justify-center gap-2 px-4 pt-5" data-testid="funnel-stages">
          {stages.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${i + stageOffset < step ? "bg-emerald-500" : i + stageOffset === step ? "bg-brand-indigo" : "bg-navy/15"}`} />
              <span className={`text-[10.5px] font-bold sm:text-[11.5px] ${i + stageOffset === step ? "text-navy" : "text-navy/40"}`}>{s}</span>
              {i < stages.length - 1 && <span className="mx-0.5 h-px w-4 bg-navy/10 sm:w-6" />}
            </div>
          ))}
        </div>
      )}
      {/* progress */}
      {step < 3 && !disqualified && !straightToCalendar && (
        <div className="mx-6 mt-4 h-1.5 rounded-full bg-navy/5 sm:mx-8">
          <div className="sl-progress-shine h-full rounded-full bg-brand-gradient transition-all duration-500" style={{ width: `${Math.max(progress, 0.04) * 100}%` }} />
        </div>
      )}
      {body}
    </div>
  );
}
